import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import api from '../lib/api';
import toast from 'react-hot-toast';
import LoadingSpinner from '@shared/ui/LoadingSpinner';

const NoteShortUrlPage: React.FC = () => {
  const { shortId } = useParams<{ shortId: string }>();
  const navigate = useNavigate();

  const { data, isLoading, error } = useQuery(
    ['note-short-url', shortId],
    async () => {
      const response = await api.get(`/public/note-feedback/short/${shortId}`);
      return response.data;
    },
    {
      enabled: !!shortId,
      retry: false,
      refetchOnWindowFocus: false,
    }
  );

  useEffect(() => {
    if (data?.success && data?.data?.token) {
      // Redirect to the feedback page with the full token
      navigate(`/feedback/${data.data.token}`, { replace: true });
    } else if (error) {
      toast.error('Share link not found, expired, or revoked');
      navigate('/', { replace: true });
    }
  }, [data, error, navigate]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background dark:bg-background-dark-primary">
        <div className="text-center">
          <LoadingSpinner size="xl" className="mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-text-primary dark:text-text-dark-primary mb-2">
            Loading...
          </h2>
          <p className="text-text-secondary dark:text-text-dark-secondary">
            Redirecting you to the shared note
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background dark:bg-background-dark-primary">
        <div className="text-center">
          <div className="text-error-500 text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-text-primary dark:text-text-dark-primary mb-4">
            Share Link Not Found
          </h2>
          <p className="text-text-secondary dark:text-text-dark-secondary mb-6">
            This share link is invalid, expired, or has been revoked.
          </p>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            Go to Homepage
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background dark:bg-background-dark-primary">
      <div className="text-center">
        <LoadingSpinner size="xl" className="mx-auto mb-4" />
        <h2 className="text-lg font-semibold text-text-primary dark:text-text-dark-primary mb-2">
          Redirecting...
        </h2>
      </div>
    </div>
  );
};

export default NoteShortUrlPage;

